import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertCircle, BookOpen, Compass, Eye, Heart, Lightbulb, Map, MapPin,
  Navigation, RefreshCw, Sparkles, Target, Users, Wand2
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { ScrollArea } from "../ui/scroll-area";

export const StoryNavigatorModal = ({
  open,
  onOpenChange,
  project,
  projectId,
  API,
  storyAnalysis,
  analyzingStory,
  weaving,
  weaveDirection,
  setWeaveDirection,
  weaveLength,
  setWeaveLength,
  handleAnalyzeStoryPosition,
  handleWeaveContent,
}) => {
  return (
            <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-5xl flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-literary flex items-center gap-2">
              <Map className="w-5 h-5 text-emerald-600" />
              Story Navigator
            </DialogTitle>
            <DialogDescription>
              See where you are in your story and guide what comes next
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="flex-1 pr-4">
            <div className="space-y-4 py-4">
              {/* Analyze Button */}
              {!storyAnalysis && !analyzingStory && (
                <div className="text-center py-8 bg-background rounded-lg">
                  <Map className="w-12 h-12 text-emerald-600 mb-4" />
                  <h3 className="font-semibold text-lg mb-2">Where Are We Now?</h3>
                  <p className="text-muted-foreground mb-4">Let Shweebum analyze your story and show you the way forward</p>
                  <Button 
                    onClick={handleAnalyzeStoryPosition}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    disabled={!project?.content}
                  >
                    Analyze My Story
                  </Button>
                </div>
              )}
              
              {analyzingStory && (
                <div className="text-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin text-emerald-600 mb-4" />
                  <p className="text-muted-foreground">Analyzing your story...</p>
                </div>
              )}
              
              {storyAnalysis && (
                <>
                  {/* Progress Bar */}
                  <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg p-4 border border-emerald-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-emerald-800">Story Progress</span>
                      <span className="text-emerald-700 font-bold">{storyAnalysis.story_progress}%</span>
                    </div>
                    <div className="w-full bg-emerald-100 rounded-full h-3">
                      <div 
                        className="bg-gradient-to-r from-emerald-500 to-teal-500 h-3 rounded-full transition-all"
                        style={{ width: `${storyAnalysis.story_progress}%` }}
                      />
                    </div>
                    <p className="text-sm text-emerald-700 mt-2">
                      {storyAnalysis.word_count?.toLocaleString()} words written
                    </p>
                  </div>
                  
                  {/* Current Position */}
                  <Card className="bg-card border-emerald-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Navigation className="w-4 h-4 text-emerald-600" />
                        Where We Are
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="font-medium text-foreground">{storyAnalysis.current_position}</p>
                      <p className="text-sm text-muted-foreground mt-2">{storyAnalysis.summary}</p>
                    </CardContent>
                  </Card>
                  
                  {/* Recent Events */}
                  {storyAnalysis.recent_events?.length > 0 && (
                    <Card className="bg-card">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">Recent Events</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-1">
                          {storyAnalysis.recent_events.map((event, idx) => (
                            <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                              <span className="text-emerald-500">•</span> {event}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}
                  
                  {/* Unresolved Threads */}
                  {storyAnalysis.unresolved_threads?.length > 0 && (
                    <Card className="bg-slate-800/50 border-cyan-500/25">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 text-cyan-400" />
                          Open Threads
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-1">
                          {storyAnalysis.unresolved_threads.map((thread, idx) => (
                            <li key={idx} className="text-sm text-cyan-200 flex items-start gap-2">
                              <span className="text-cyan-400">→</span> {thread}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}
                  
                  {/* Suggested Next Steps */}
                  {storyAnalysis.suggested_next?.length > 0 && (
                    <Card className="bg-blue-50 border-blue-200">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Lightbulb className="w-4 h-4 text-blue-600" />
                          Suggested Next Steps
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {storyAnalysis.suggested_next.map((suggestion, idx) => (
                            <button
                              key={idx}
                              className="w-full text-left p-2 rounded bg-card hover:bg-blue-100 border border-blue-200 text-sm transition-colors"
                              onClick={() => setWeaveDirection(suggestion)}
                            >
                              <span className="text-blue-700">{idx + 1}.</span> {suggestion}
                            </button>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  
                  {/* Weave New Content */}
                  <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-300">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Wand2 className="w-4 h-4 text-emerald-600" />
                        Write What Happens Next
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <Label>Your Direction</Label>
                        <Textarea
                          placeholder="Describe what should happen next... (e.g., 'Jack discovers the body is actually a decoy and realizes he's been set up')"
                          value={weaveDirection}
                          onChange={(e) => setWeaveDirection(e.target.value)}
                          className="mt-1 h-20 bg-card"
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Length Type</Label>
                          <Select 
                            value={weaveLength.type} 
                            onValueChange={(v) => setWeaveLength({...weaveLength, type: v})}
                          >
                            <SelectTrigger className="bg-card">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="paragraphs">Paragraphs</SelectItem>
                              <SelectItem value="words">Words</SelectItem>
                              <SelectItem value="pages">Pages</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Amount</Label>
                          <Select 
                            value={String(weaveLength.value)} 
                            onValueChange={(v) => setWeaveLength({...weaveLength, value: parseInt(v)})}
                          >
                            <SelectTrigger className="bg-card">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1">1</SelectItem>
                              <SelectItem value="2">2</SelectItem>
                              <SelectItem value="3">3</SelectItem>
                              <SelectItem value="5">5</SelectItem>
                              <SelectItem value="10">10</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <Button 
                        onClick={handleWeaveContent}
                        disabled={weaving || !weaveDirection.trim()}
                        className="w-full bg-emerald-600 hover:bg-emerald-700"
                      >
                        {weaving ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Weaving into story...
                          </>
                        ) : (
                          <>
                            <Wand2 className="w-4 h-4 mr-2" />
                            Add to Story
                          </>
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                  
                  {/* Refresh Analysis */}
                  <Button 
                    variant="outline" 
                    onClick={handleAnalyzeStoryPosition}
                    disabled={analyzingStory}
                    className="w-full"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${analyzingStory ? 'animate-spin' : ''}`} />
                    Refresh Analysis
                  </Button>
                </>
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>


  );
};
